import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  getRouteCalendar, getRouteDay, getClients, getDueClients,
  addStopToShift, moveStopToShift, deleteClientVisit, reorderRoute,
  autoBuildDay, approveRoute, unapproveRoute
} from './api';
import { useTranslation } from '../i18n';
import WeekView from './WeekView';

const SLOT = {
  morning: { color: '#f59e0b', head: 'linear-gradient(90deg,#fef3c7,#fef9d9)', text: '#92400e', icon: '☀️' },
  afternoon: { color: '#2c6e9e', head: 'linear-gradient(90deg,#ecf3fb,#f0f6fa)', text: '#1e5e9c', icon: '🌤️' },
  evening: { color: '#7b3fb0', head: 'linear-gradient(90deg,#f3eafa,#f6eef9)', text: '#6b30a3', icon: '🌙' }
};
const SHIFTS = ['morning', 'afternoon', 'evening'];
const DOW = { en: ['S', 'M', 'T', 'W', 'T', 'F', 'S'], es: ['D', 'L', 'M', 'M', 'J', 'V', 'S'] };

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayYmd = () => ymd(new Date());
const parseYmd = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const fmt = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;

export default function RouteCalendar({ role, onOpenRoute, onDateChange }) {
  const { t, lang } = useTranslation();
  const locale = lang === 'es' ? 'es' : 'en-US';
  const canEdit = role === 'owner' || role === 'manager';
  const slotLabel = (s) => t(`cal.${s}`);
  const prettyDate = (s) => parseYmd(s).toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric' });

  const [anchor, setAnchor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [summaries, setSummaries] = useState({});
  const [selected, setSelected] = useState(todayYmd());
  const [dayRoutes, setDayRoutes] = useState([]);
  const [loadingDay, setLoadingDay] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [drag, setDrag] = useState(null); // { stopId, fromSlot, index }
  const [view, setView] = useState('month'); // 'month' | 'week'
  const [moveOpen, setMoveOpen] = useState(null); // stopId whose Move popover is open
  const [moveDate, setMoveDate] = useState(null); // date chosen in the Move popover
  const [allClients, setAllClients] = useState([]);
  const [dueIds, setDueIds] = useState(new Set());
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const moveTriggerRef = useRef(null);
  const movePopRef = useRef(null);

  // Escape closes the Move popover, restores focus to the trigger button.
  useEffect(() => {
    if (!moveOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setMoveOpen(null);
        if (moveTriggerRef.current) moveTriggerRef.current.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    // Move focus into the popover (the date input is the first interactive
    // element).
    const firstInput = movePopRef.current?.querySelector('input,button');
    if (firstInput) firstInput.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [moveOpen]);

  const gridStart = new Date(anchor); gridStart.setDate(1 - anchor.getDay());
  const cells = Array.from({ length: 42 }, (_, i) => { const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d; });

  const loadMonth = useCallback(async () => {
    const gs = new Date(anchor); gs.setDate(1 - anchor.getDay());
    const ge = new Date(gs); ge.setDate(gs.getDate() + 41);
    try {
      const { data } = await getRouteCalendar(ymd(gs), ymd(ge));
      const by = {};
      (data || []).forEach((r) => { (by[r.route_date] = by[r.route_date] || []).push(r); });
      setSummaries(by);
    } catch (e) { setError(t('cal.err_load')); }
  }, [anchor, t]);

  const loadDay = useCallback(async (date) => {
    setLoadingDay(true);
    try {
      const { data } = await getRouteDay(date);
      setDayRoutes((data || []).slice().sort((a, b) => SHIFTS.indexOf(a.time_slot) - SHIFTS.indexOf(b.time_slot)));
    } catch (e) {
      setDayRoutes([]);
      setError(t('cal.err_day'));
    }
    setLoadingDay(false);
  }, [t]);

  // All clients (for the add picker) — loaded once. Due ids refresh per date.
  useEffect(() => {
    if (!canEdit) return;
    getClients().then(({ data }) => setAllClients(data || [])).catch(() => {});
  }, [canEdit]);
  useEffect(() => {
    if (!canEdit) return;
    getDueClients(selected).then(({ data }) => setDueIds(new Set((data || []).map((c) => c.id)))).catch(() => setDueIds(new Set()));
  }, [selected, canEdit]);

  useEffect(() => { loadMonth(); }, [loadMonth]);
  useEffect(() => { loadDay(selected); }, [selected, loadDay]);
  // Keep the Route tab pointed at whatever day is open in the calendar, so
  // switching tabs shows the same date you were planning (not just today).
  useEffect(() => { if (onDateChange) onDateChange(selected); }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = async () => { await Promise.all([loadMonth(), loadDay(selected)]); };

  // ── Autosave actions (each persists immediately, then re-syncs the day) ──
  const run = async (fn, errKey) => {
    setBusy(true);
    try { await fn(); await refresh(); }
    catch (e) { setError(e.response?.data?.error || t(errKey)); }
    setBusy(false);
  };
  const addToShift = (client, slot) => run(() => addStopToShift(selected, client.id, slot), 'cal.err_add');
  const moveToShift = (stopId, slot, date) => run(() => moveStopToShift(stopId, slot, date), 'cal.err_move');
  const openMove = (stop) => { setMoveOpen(moveOpen === stop.id ? null : stop.id); setMoveDate(selected); };
  const doMove = (stop, slot) => { setMoveOpen(null); moveToShift(stop.id, slot, moveDate); };
  const removeStop = (stop) => run(() => deleteClientVisit(stop.id), 'cal.err_remove');
  const autoBuild = () => run(async () => { await autoBuildDay(selected); }, 'cal.err_autobuild');

  const approveDay = () => {
    const toApprove = dayRoutes.filter((r) => !r.approved && (r.route_stops || []).length > 0);
    if (toApprove.length === 0) { setError(t('cal.err_nothing_approve')); return; }
    run(async () => { for (const r of toApprove) await approveRoute(r.id); }, 'cal.err_approve_day');
  };
  const toggleApprove = (r) => {
    if (!r.approved && (r.route_stops || []).length === 0) { setError(t('cal.err_add_stops')); return; }
    run(() => (r.approved ? unapproveRoute(r.id) : approveRoute(r.id)), 'cal.err_update');
  };

  // Reorder within a shift — optimistic, preserves payments/approval.
  const reorder = async (route, fromIdx, toIdx) => {
    const stops = (route.route_stops || []).slice().sort((a, b) => a.stop_number - b.stop_number);
    if (toIdx < 0 || toIdx >= stops.length || fromIdx === toIdx) return;
    const [moved] = stops.splice(fromIdx, 1);
    stops.splice(toIdx, 0, moved);
    const renum = stops.map((s, i) => ({ ...s, stop_number: i + 1 }));
    setDayRoutes((prev) => prev.map((r) => (r.id === route.id ? { ...r, route_stops: renum } : r)));
    try { await reorderRoute(route.id, renum.map((s) => s.id)); }
    catch (e) { setError(t('cal.err_reorder')); loadDay(selected); }
  };
  // Drop on a specific stop row: same shift → reorder; other shift → move here.
  const dropOnRow = (e, route, slot, toIdx) => {
    e.stopPropagation();
    if (!drag) return;
    if (drag.fromSlot === slot && route) reorder(route, drag.index, toIdx);
    else moveToShift(drag.stopId, slot);
    setDrag(null);
  };
  // Drop anywhere on a shift card (incl. empty shifts) → move into that shift.
  const dropOnShift = (slot) => {
    if (!drag) return;
    if (drag.fromSlot !== slot) moveToShift(drag.stopId, slot);
    setDrag(null);
  };

  // Build a shift -> route lookup so every shift renders even when empty.
  const routeBySlot = {};
  dayRoutes.forEach((r) => { if (SHIFTS.includes(r.time_slot)) routeBySlot[r.time_slot] = r; });

  const onDayIds = new Set();
  dayRoutes.forEach((r) => (r.route_stops || []).forEach((s) => onDayIds.add(s.client_id)));
  const q = search.trim().toLowerCase();
  const available = allClients
    .filter((c) => !onDayIds.has(c.id))
    .filter((c) => !q || `${c.name} ${c.street || ''} ${c.city || ''}`.toLowerCase().includes(q))
    .sort((a, b) => (dueIds.has(b.id) ? 1 : 0) - (dueIds.has(a.id) ? 1 : 0) || (a.name || '').localeCompare(b.name || ''))
    .slice(0, 40);

  let totalStops = 0, totalCharged = 0;
  dayRoutes.forEach((r) => (r.route_stops || []).forEach((s) => { totalStops += 1; totalCharged += parseFloat(s.amount_charged) || 0; }));
  const stopsLabel = (n) => t(n === 1 ? 'cal.stops_one' : 'cal.stops_many', { n });

  return (
    <div style={styles.wrap}>
      <div style={styles.viewToggle} role="tablist" aria-label={t('cal.view_month') + ' / ' + t('cal.view_week')}>
        <button
          role="tab"
          aria-selected={view === 'month'}
          style={{ ...styles.viewBtn, ...(view === 'month' ? styles.viewBtnActive : {}) }}
          onClick={() => setView('month')}
        >{t('cal.view_month')}</button>
        <button
          role="tab"
          aria-selected={view === 'week'}
          style={{ ...styles.viewBtn, ...(view === 'week' ? styles.viewBtnActive : {}) }}
          onClick={() => setView('week')}
        >{t('cal.view_week')}</button>
      </div>

      {view === 'week' ? (
        <WeekView role={role} onOpenRoute={onOpenRoute} />
      ) : (
      <>
      {error && (
        <div
          style={styles.error}
          role="alert"
          tabIndex={0}
          onClick={() => setError('')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setError(''); } }}
        >
          {error}
        </div>
      )}
      <div style={styles.split}>
        {/* Left: mini calendar */}
        <div style={styles.left}>
          <div style={styles.miniNav}>
            <button
              style={styles.arrow}
              aria-label={t('a11y.previous_month')}
              onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))}
            ><span aria-hidden="true">‹</span></button>
            <span style={styles.mh} aria-live="polite">{anchor.toLocaleDateString(locale, { month: 'long', year: 'numeric' })}</span>
            <button
              style={styles.arrow}
              aria-label={t('a11y.next_month')}
              onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))}
            ><span aria-hidden="true">›</span></button>
          </div>
          <button style={styles.todayBtn} onClick={() => { const d = new Date(); setAnchor(new Date(d.getFullYear(), d.getMonth(), 1)); setSelected(todayYmd()); }}>{t('cal.today')}</button>

          <div style={styles.mdow} aria-hidden="true">{DOW[lang === 'es' ? 'es' : 'en'].map((d, i) => <span key={i} style={styles.mdowCell}>{d}</span>)}</div>
          <div style={styles.mgrid} role="grid" aria-label={anchor.toLocaleDateString(locale, { month: 'long', year: 'numeric' })}>
            {cells.map((d) => {
              const key = ymd(d);
              const inMonth = d.getMonth() === anchor.getMonth();
              const isToday = key === todayYmd();
              const isSel = key === selected;
              const routes = summaries[key] || [];
              const cellLabel = t('a11y.day_of_week', { date: d.toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric' }) }) + (routes.length > 0 ? `, ${routes.length} ${stopsLabel(routes.reduce((n, r) => n + (r.route_stops || []).length, 0)).replace(/^[0-9]+\s*/, '')}` : '');
              return (
                <button
                  key={key}
                  onClick={() => { setSelected(key); setShowAdd(false); }}
                  aria-label={cellLabel}
                  aria-pressed={isSel}
                  aria-current={isToday ? 'date' : undefined}
                  style={{ ...styles.mc, ...(inMonth ? {} : styles.mcOut), ...(isToday && !isSel ? styles.mcToday : {}), ...(isSel ? styles.mcSel : {}) }}
                >
                  {d.getDate()}
                  <span style={styles.dots} aria-hidden="true">
                    {SHIFTS.filter((s) => routes.some((r) => r.time_slot === s)).map((s) => (
                      <i key={s} style={{ ...styles.dot, background: isSel ? '#fff' : SLOT[s].color }} />
                    ))}
                  </span>
                </button>
              );
            })}
          </div>

          <div style={styles.legend} aria-hidden="true">
            {SHIFTS.map((s) => <span key={s} style={styles.lg}><i style={{ ...styles.ldot, background: SLOT[s].color }} />{slotLabel(s)}</span>)}
          </div>
          <div style={styles.note}>{t('cal.note')}</div>
        </div>

        {/* Right: day builder */}
        <div style={styles.right}>
          <div style={styles.dayHead}>
            <div>
              <div style={styles.dt}>{prettyDate(selected)}</div>
              <div style={styles.dsub}>{stopsLabel(totalStops)}{totalCharged > 0 ? ` · ${t('cal.expected', { amount: fmt(totalCharged) })}` : ''}</div>
            </div>
            {canEdit && (
              <div style={styles.headBtns}>
                <button style={styles.ghostBtn} disabled={busy} onClick={autoBuild}>{t('cal.autobuild')}</button>
                <button style={styles.ghostBtn} disabled={busy} onClick={() => setShowAdd((v) => !v)}>{showAdd ? t('cal.done_adding') : t('cal.add_clients')}</button>
                {dayRoutes.length > 0 && <button style={styles.approveAll} disabled={busy} onClick={approveDay}>{t('cal.approve_day')}</button>}
              </div>
            )}
          </div>

          {/* Add-clients picker */}
          {canEdit && showAdd && (
            <div style={styles.addPanel}>
              <label htmlFor="route-cal-search" style={styles.srOnly}>{t('a11y.search_clients')}</label>
              <input
                id="route-cal-search"
                style={styles.search}
                placeholder={t('cal.search_clients')}
                aria-label={t('a11y.search_clients')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div style={styles.addList} role="list">
                {available.length === 0 && <div style={styles.muted}>{t('cal.no_available')}</div>}
                {available.map((c) => (
                  <div key={c.id} style={styles.addRow} role="listitem">
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={styles.addName}>{c.name}{dueIds.has(c.id) && <span style={styles.dueBadge}>{t('cal.due')}</span>}</div>
                      <div style={styles.addAddr}>{c.street}{c.city ? `, ${c.city}` : ''}</div>
                    </div>
                    <div style={styles.addBtns} role="group" aria-label={t('a11y.add_to_shift', { shift: c.name })}>
                      {SHIFTS.map((s) => (
                        <button
                          key={s}
                          style={{ ...styles.shiftAdd, color: SLOT[s].text }}
                          disabled={busy}
                          aria-label={t('a11y.add_to_shift', { shift: `${c.name} — ${slotLabel(s)}` })}
                          title={`${slotLabel(s)}`}
                          onClick={() => addToShift(c, s)}
                        >
                          <span aria-hidden="true">{SLOT[s].icon}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={styles.body}>
            {loadingDay ? (
              <div style={styles.muted} role="status" aria-live="polite">{t('cal.loading')}</div>
            ) : (
              <>
                {SHIFTS.map((slot) => {
                  const r = routeBySlot[slot];
                  const meta = SLOT[slot];
                  const stops = r ? (r.route_stops || []).slice().sort((a, b) => a.stop_number - b.stop_number) : [];
                  const movePopId = `move-pop-${slot}`;
                  return (
                    <div key={slot} style={styles.shift}>
                      <div style={{ ...styles.sh, background: meta.head }}>
                        <span style={styles.si} aria-hidden="true">{meta.icon}</span>
                        <span style={{ ...styles.sl, color: meta.text }}>{slotLabel(slot)}</span>
                        <span style={{ ...styles.scount, background: meta.color }} aria-label={`${stops.length} ${stopsLabel(stops.length).replace(/^[0-9]+\s*/, '')}`}>{stops.length}</span>
                        <span style={{ flex: 1 }} />
                        {r && stops.length > 0 && (
                          <span style={{ ...styles.st, ...(r.approved ? styles.stApproved : styles.stDraft) }}>{r.approved ? t('cal.approved') : t('cal.draft')}</span>
                        )}
                        {canEdit && r && stops.length > 0 && (
                          <button style={styles.smallBtn} disabled={busy} onClick={() => toggleApprove(r)} aria-pressed={r.approved}>
                            {r.approved ? t('cal.unapprove') : t('cal.approve')}
                          </button>
                        )}
                      </div>
                      <div
                        role="region"
                        aria-label={t('a11y.drop_zone_shift', { shift: slotLabel(slot) })}
                        style={{ ...styles.stops, ...(drag && drag.fromSlot !== slot ? styles.shiftDropTarget : {}) }}
                        onDragOver={(e) => { if (canEdit && drag) e.preventDefault(); }}
                        onDrop={() => canEdit && dropOnShift(slot)}
                      >
                        {stops.length === 0 && <div style={styles.emptyShift}>{drag && drag.fromSlot !== slot ? t('cal.move_to') + ' ' + slotLabel(slot) : t('cal.empty_shift')}</div>}
                        {stops.map((s, i) => (
                          <React.Fragment key={s.id}>
                            <div
                              onDragOver={(e) => { if (canEdit && drag) e.preventDefault(); }}
                              onDrop={(e) => canEdit && dropOnRow(e, r, slot, i)}
                              style={{ ...styles.stop2, ...(drag && drag.stopId === s.id ? styles.stopDragging : {}) }}
                            >
                              {canEdit && (
                                <span
                                  draggable
                                  onDragStart={(e) => {
                                    // Safari/Firefox won't start a drag unless setData is called.
                                    e.dataTransfer.setData('text/plain', s.id);
                                    e.dataTransfer.effectAllowed = 'move';
                                    setDrag({ stopId: s.id, fromSlot: slot, index: i });
                                  }}
                                  onDragEnd={() => setDrag(null)}
                                  style={styles.dragHandle}
                                  aria-label={t('a11y.drag_to_reorder')}
                                  role="button"
                                  title={t('a11y.drag_to_reorder')}
                                >
                                  <span aria-hidden="true">⠿</span>
                                </span>
                              )}
                              <span style={styles.num} aria-hidden="true">{i + 1}</span>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={styles.nm}>
                                  {s.clients?.name || 'Client'}
                                  {s.completed && <span style={styles.doneTag} aria-label={t('cal.approved')}><span aria-hidden="true">✓</span></span>}
                                </div>
                                <div style={styles.ad}>{s.clients?.street}{s.clients?.city ? `, ${s.clients.city}` : ''}</div>
                              </div>
                              {canEdit && (
                                <span style={styles.rowActions} role="group" aria-label={t('a11y.client_actions_for', { name: s.clients?.name || 'Client' })}>
                                  <span style={styles.reorderCol}>
                                    <button
                                      style={styles.reBtn}
                                      aria-label={t('a11y.move_up')}
                                      title={t('cal.move_up')}
                                      disabled={busy || i === 0}
                                      onClick={() => reorder(r, i, i - 1)}
                                    ><span aria-hidden="true">↑</span></button>
                                    <button
                                      style={styles.reBtn}
                                      aria-label={t('a11y.move_down')}
                                      title={t('cal.move_down')}
                                      disabled={busy || i === stops.length - 1}
                                      onClick={() => reorder(r, i, i + 1)}
                                    ><span aria-hidden="true">↓</span></button>
                                  </span>
                                  <button
                                    ref={moveOpen === s.id ? moveTriggerRef : null}
                                    style={{ ...styles.moveBtn, ...(moveOpen === s.id ? styles.moveBtnActive : {}) }}
                                    disabled={busy}
                                    aria-label={t('a11y.move_stop')}
                                    aria-haspopup="dialog"
                                    aria-expanded={moveOpen === s.id}
                                    aria-controls={moveOpen === s.id ? movePopId : undefined}
                                    title={t('cal.move')}
                                    onClick={() => openMove(s)}
                                  ><span aria-hidden="true">↔</span></button>
                                  <button
                                    style={styles.delBtn}
                                    disabled={busy}
                                    aria-label={t('a11y.remove_stop')}
                                    title={t('cal.remove')}
                                    onClick={() => removeStop(s)}
                                  ><span aria-hidden="true">✕</span></button>
                                </span>
                              )}
                            </div>
                            {canEdit && moveOpen === s.id && (
                              <div
                                id={movePopId}
                                role="dialog"
                                aria-modal="false"
                                aria-label={t('cal.move')}
                                ref={movePopRef}
                                style={styles.movePop}
                              >
                                <label htmlFor={`${movePopId}-date`} style={styles.movePopLabel}>{t('cal.move')}:</label>
                                <input
                                  id={`${movePopId}-date`}
                                  type="date"
                                  style={styles.movePopDate}
                                  value={moveDate || selected}
                                  onChange={(e) => setMoveDate(e.target.value)}
                                />
                                {SHIFTS.map((o) => {
                                  const isCurrent = o === slot && (moveDate || selected) === selected;
                                  return (
                                    <button key={o} disabled={busy || isCurrent}
                                      style={{ ...styles.movePopBtn, ...(isCurrent ? styles.movePopBtnCurrent : {}) }}
                                      onClick={() => doMove(s, o)}>
                                      <span aria-hidden="true">{SLOT[o].icon} </span>{slotLabel(o)}
                                    </button>
                                  );
                                })}
                                <button
                                  style={styles.movePopClose}
                                  aria-label={t('a11y.close_dialog')}
                                  onClick={() => { setMoveOpen(null); if (moveTriggerRef.current) moveTriggerRef.current.focus(); }}
                                ><span aria-hidden="true">✕</span></button>
                              </div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  );
                })}
                <div style={styles.footRow}>
                  {canEdit && <span style={styles.autosaveHint} role="status" aria-live="polite"><span aria-hidden="true">✓ </span>{t('cal.autosaved')}</span>}
                  <button style={styles.openBtn} onClick={() => onOpenRoute && onOpenRoute(selected)}>{t('cal.open_route')}</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 12 },
  viewToggle: { display: 'inline-flex', gap: 2, background: '#f3f4ef', borderRadius: 10, padding: 4, alignSelf: 'flex-start' },
  viewBtn: { padding: '7px 16px', borderRadius: 7, border: 'none', background: 'transparent', fontSize: 13, fontWeight: 700, color: '#5f6b58', cursor: 'pointer', fontFamily: 'inherit' },
  viewBtnActive: { background: '#fff', color: '#14653a', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  error: { background: '#FEF2F2', color: '#dc2626', borderRadius: 8, padding: '10px 12px', fontSize: 13, cursor: 'pointer', position: 'sticky', top: 8, zIndex: 30, border: '1px solid #f3c0c0', boxShadow: '0 4px 14px rgba(0,0,0,0.08)' },
  split: { display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' },
  left: { width: 320, flexShrink: 0, background: '#fff', border: '1px solid #e3e9df', borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 },
  miniNav: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  arrow: { width: 30, height: 30, border: '1px solid #e3e9df', borderRadius: 8, background: '#fff', color: '#14653a', fontSize: 15, fontWeight: 800, cursor: 'pointer' },
  mh: { fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 16, color: '#1a1a1a', textTransform: 'capitalize' },
  todayBtn: { alignSelf: 'flex-start', fontSize: 12, fontWeight: 700, color: '#5f6b58', border: '1px solid #e3e9df', borderRadius: 8, padding: '6px 12px', background: '#fff', cursor: 'pointer' },
  mdow: { display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 },
  mdowCell: { textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#5f6b58' },
  mgrid: { display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 3 },
  mc: { aspectRatio: '1', borderRadius: 8, border: 'none', background: 'none', position: 'relative', fontSize: 12, fontWeight: 600, color: '#1a1a1a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  mcOut: { color: '#9aa394' },
  mcToday: { background: '#f4fbf5', border: '1px solid #bfe2c8', color: '#14653a', fontWeight: 800 },
  mcSel: { background: '#14653a', color: '#fff', fontWeight: 800 },
  dots: { position: 'absolute', bottom: 4, display: 'flex', gap: 2 },
  dot: { width: 4, height: 4, borderRadius: '50%' },
  legend: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  lg: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: '#5f6b58' },
  ldot: { width: 8, height: 8, borderRadius: '50%' },
  note: { fontSize: 11.5, color: '#5f6b58', lineHeight: 1.5, background: '#f4f8f1', border: '1px solid #e6efe3', borderRadius: 10, padding: '10px 12px' },

  right: { flex: 1, minWidth: 320, background: '#fff', border: '1px solid #e3e9df', borderRadius: 14, overflow: 'hidden' },
  dayHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', borderBottom: '1px solid #eef1ec', gap: 12, flexWrap: 'wrap' },
  dt: { fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 19, letterSpacing: '-0.4px', color: '#1a1a1a', textTransform: 'capitalize' },
  dsub: { fontSize: 12.5, color: '#5f6b58', marginTop: 2 },
  headBtns: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  ghostBtn: { border: '1px solid #cfe9d4', background: '#fff', color: '#14653a', fontWeight: 700, fontSize: 12.5, borderRadius: 9, padding: '8px 12px', cursor: 'pointer' },
  approveAll: { background: '#14653a', color: '#fff', fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 13, border: 'none', borderRadius: 9, padding: '9px 14px', cursor: 'pointer' },

  addPanel: { padding: '14px 18px', borderBottom: '1px solid #eef1ec', background: '#fbfdfa' },
  search: { width: '100%', padding: '10px 12px', border: '1px solid #e3e9df', borderRadius: 9, fontSize: 14, boxSizing: 'border-box', outline: 'none', marginBottom: 8 },
  addList: { maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 },
  addRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px', borderRadius: 8, border: '1px solid #f0f2ec' },
  addName: { fontSize: 13, fontWeight: 700, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 6 },
  addAddr: { fontSize: 11, color: '#5f6b58', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  dueBadge: { fontSize: 9.5, fontWeight: 800, color: '#b7791f', background: '#fdf3e0', borderRadius: 100, padding: '1px 7px', textTransform: 'uppercase' },
  addBtns: { display: 'flex', gap: 4, flexShrink: 0 },
  shiftAdd: { width: 30, height: 30, border: '1px solid #e3e9df', background: '#fff', borderRadius: 8, fontSize: 14, cursor: 'pointer' },

  body: { padding: 18, display: 'flex', flexDirection: 'column', gap: 14 },
  muted: { fontSize: 13, color: '#5f6b58', padding: '6px 2px' },
  shift: { border: '1px solid #eef1ec', borderRadius: 13, overflow: 'hidden' },
  sh: { display: 'flex', alignItems: 'center', gap: 9, padding: '11px 14px' },
  si: { fontSize: 15 },
  sl: { fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 14 },
  scount: { color: '#fff', fontSize: 11, fontWeight: 800, borderRadius: 100, padding: '2px 9px' },
  st: { fontSize: 10.5, fontWeight: 800, borderRadius: 100, padding: '3px 9px', textTransform: 'uppercase', letterSpacing: '0.02em' },
  stApproved: { background: '#ecf8ee', color: '#14653a' },
  stDraft: { background: '#f0f2ec', color: '#5f6b58' },
  smallBtn: { fontSize: 11, fontWeight: 700, color: '#5f6b58', border: '1px solid #e3e9df', borderRadius: 7, padding: '5px 10px', background: '#fff', cursor: 'pointer' },
  stops: { padding: '4px 12px 12px', transition: 'background 0.12s' },
  shiftDropTarget: { background: '#f0fbf2', outline: '2px dashed #9bd9ab', outlineOffset: -4 },
  emptyShift: { fontSize: 12, color: '#5f6b58', padding: '12px 6px', textAlign: 'center', fontStyle: 'italic' },
  stop2: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 6px', borderTop: '1px solid #f4f6f2', borderRadius: 8 },
  stopDragging: { opacity: 0.45, background: '#f4f8f1' },
  dragHandle: { cursor: 'grab', color: '#5f6b58', fontSize: 15, lineHeight: 1, userSelect: 'none', flexShrink: 0, padding: '0 2px', display: 'inline-flex', alignItems: 'center' },
  num: { width: 22, height: 22, borderRadius: '50%', background: '#14653a', color: '#fff', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  nm: { fontSize: 13, fontWeight: 600, color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: 6 },
  ad: { fontSize: 11, color: '#5f6b58', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  doneTag: { color: '#14653a', fontWeight: 800, fontSize: 12 },
  rowActions: { display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 },
  reorderCol: { display: 'flex', flexDirection: 'column', gap: 2 },
  reBtn: { width: 24, height: 17, border: '1px solid #e3e9df', background: '#fff', borderRadius: 5, fontSize: 10, lineHeight: 1, color: '#5f6b58', cursor: 'pointer', padding: 0 },
  moveBtn: { width: 28, height: 28, border: '1px solid #e3e9df', background: '#fff', borderRadius: 7, fontSize: 14, cursor: 'pointer', padding: 0, color: '#14653a' },
  moveBtnActive: { background: '#ecf8ee', borderColor: '#9bd9ab' },
  delBtn: { width: 28, height: 28, border: '1px solid #f2c9c9', background: '#fff', borderRadius: 7, fontSize: 12, color: '#b91c1c', cursor: 'pointer', padding: 0 },
  movePop: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '2px 6px 8px 34px', padding: '10px 12px', background: '#f4f8f1', border: '1px solid #cfe9d4', borderRadius: 10 },
  movePopLabel: { fontSize: 12, fontWeight: 700, color: '#14653a' },
  movePopDate: { padding: '6px 8px', border: '1px solid #cfe9d4', borderRadius: 7, fontSize: 13, color: '#1a1a1a', background: '#fff' },
  movePopBtn: { padding: '6px 10px', border: '1px solid #cfe9d4', background: '#fff', borderRadius: 7, fontSize: 12, fontWeight: 600, color: '#14653a', cursor: 'pointer' },
  movePopBtnCurrent: { opacity: 0.4, cursor: 'default' },
  footRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' },
  autosaveHint: { fontSize: 11.5, color: '#14653a', fontWeight: 600 },
  openBtn: { border: '1px solid #cfe9d4', background: '#fff', color: '#14653a', fontWeight: 700, fontSize: 13, borderRadius: 9, padding: '10px 14px', cursor: 'pointer' },
  movePopClose: { marginLeft: 'auto', width: 24, height: 24, border: '1px solid #cfe9d4', background: '#fff', borderRadius: 6, fontSize: 11, color: '#14653a', cursor: 'pointer', padding: 0 },
  srOnly: { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }
};
